import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import { qualificationApi } from '../qualificationApi';
import MoneyInput from '../shared/MoneyInput';
import NumberInput from '../shared/NumberInput';
import { formatDate } from '../kit/time';
import PickSelect from '../shared/PickSelect';
import { TableScroller } from '@/components/ui/table-scroller';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../../components/ui/alert-dialog';

const CATEGORY_LABELS = {
  government: 'Government',
  conventional: 'Conventional',
  jumbo: 'Jumbo',
  non_qm: 'Non-QM',
};

const PROPERTY_TYPES = [
  ['single_family', 'Single family'], ['condo', 'Condo'], ['townhouse', 'Townhouse'],
  ['multi_unit_2_4', '2-4 units'], ['manufactured', 'Manufactured'],
];
const PROPERTY_USES = [['primary', 'Primary'], ['secondary', 'Second home'], ['investment', 'Investment']];
const MI_KINDS = [
  { value: 'pmi_grid', label: 'Private MI (by LTV and score)' },
  { value: 'fha_mip', label: 'FHA mortgage insurance' },
  { value: 'va_funding_fee', label: 'VA funding fee' },
  { value: 'none', label: 'None' },
];

const fmt$ = (n) => (n || n === 0) ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '—';

export default function LoanProgramsTab() {
  const [programs, setPrograms] = useState(null);
  const [retired, setRetired] = useState([]);
  const [retireTarget, setRetireTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [limitsUnverified, setLimitsUnverified] = useState(false);
  const [editing, setEditing] = useState(null); // program_id
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    qualificationApi.listPrograms()
      .then((d) => { setPrograms(d.programs || []); setRetired(d.retired_programs || []); setLimitsUnverified(!!d.limits_unverified); })
      .catch((e) => { toast({ title: 'Could not load programs', description: errorMessage(e) }); setPrograms([]); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (p) => {
    setEditing(p.program_id);
    setDraft({
      name: p.name,
      active: p.active,
      min_credit_score: p.min_credit_score,
      max_dti: p.max_dti,
      min_down_percent: p.min_down_percent,
      max_loan_amount: p.max_loan_amount,
      default_rate: p.default_rate,
      requires_veteran: p.requires_veteran,
      first_time_buyer_only: p.first_time_buyer_only,
      min_reserves_months: p.min_reserves_months,
      sort_order: p.sort_order,
      property_types: p.property_types || [],
      property_uses: p.property_uses || [],
      mi: p.mi,
      conditions: p.conditions || [],
    });
  };

  const toggleIn = (key, value, on) => setDraft((d) => ({
    ...d, [key]: on ? [...d[key], value] : d[key].filter((v) => v !== value),
  }));
  const setMiKind = (kind) => setDraft((d) => ({ ...d, mi: { kind, annual_rate: kind === 'fha_mip' ? d.mi?.annual_rate ?? null : null } }));

  const cancelEdit = () => { setEditing(null); setDraft({}); };

  const save = async (p) => {
    if (!draft.property_types.length || !draft.property_uses.length) {
      toast({ title: 'Pick at least one property type and one occupancy' });
      return;
    }
    const next = {
      ...draft,
      min_reserves_months: draft.min_reserves_months ?? 0, // a blank box means none
      conditions: draft.conditions.map((c) => c.trim()).filter(Boolean),
    };
    setSaving(true);
    try {
      const changed = {};
      Object.keys(next).forEach((k) => {
        if (JSON.stringify(next[k]) !== JSON.stringify(p[k])) changed[k] = next[k];
      });
      const updated = await qualificationApi.updateProgram(p.program_id, changed);
      setPrograms((prev) => prev.map((x) => x.program_id === p.program_id ? updated : x));
      toast({ title: 'Program updated' });
      cancelEdit();
    } catch (e) {
      toast({ title: 'Could not save program', description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  const toggleVerified = async (p) => {
    try {
      const updated = await qualificationApi.updateProgram(p.program_id, { verified: !p.verified_at });
      setPrograms((prev) => prev.map((x) => x.program_id === p.program_id ? updated : x));
    } catch (e) {
      toast({ title: 'Could not update verification', description: errorMessage(e) });
    }
  };

  const retire = async () => {
    setBusy(true);
    try {
      await qualificationApi.retireProgram(retireTarget.program_id);
      toast({ title: `${retireTarget.name} retired` });
      setRetireTarget(null);
      load();
    } catch (e) {
      toast({ title: 'Could not retire program', description: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  const restore = async (p) => {
    try {
      await qualificationApi.restoreProgram(p.program_id);
      toast({ title: `${p.name} restored`, description: 'It is switched off until you turn it on.' });
      load();
    } catch (e) {
      toast({ title: 'Could not restore program', description: errorMessage(e) });
    }
  };

  if (programs === null) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400" data-testid="loan-programs-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading programs...
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="loan-programs-tab">
      {limitsUnverified && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800" data-testid="limits-unverified-banner">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <span>Some program limits are unverified — confirm them against current FHFA/HUD figures before relying on results.</span>
        </div>
      )}
      <TableScroller className="relative bg-white rounded-2xl border border-slate-100 shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3 text-right min-w-[5.5rem]">Min FICO</th>
              <th className="px-4 py-3 text-right min-w-[5.5rem]">Max DTI</th>
              <th className="px-4 py-3 text-right min-w-[5.5rem]">Min Down %</th>
              <th className="px-4 py-3 text-right min-w-[5.5rem]">Max Loan</th>
              <th className="px-4 py-3 text-right min-w-[5.5rem]">Rate</th>
              <th className="px-4 py-3 text-center min-w-[4.5rem]">Active</th>
              <th className="px-4 py-3 min-w-[5rem]">Verified</th>
              <th className="px-4 py-3 text-right"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {programs.map((p) => {
              const isEditing = editing === p.program_id;
              return (
                <React.Fragment key={p.program_id}>
                <tr className="border-b border-slate-50" data-testid={`program-row-${p.code}`}>
                  <td className="px-4 py-3 font-medium" style={{ color: '#070B5F' }}>
                    {isEditing ? (
                      <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                        className="h-auto px-2 py-1 md:text-sm" data-testid={`program-name-${p.code}`} />
                    ) : p.name}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{CATEGORY_LABELS[p.category] || p.category}</td>
                  <td className="px-4 py-3 text-right">
                    {isEditing ? (
                      <NumberInput integer value={draft.min_credit_score} min={300} max={850}
                        onChange={(n) => setDraft((d) => ({ ...d, min_credit_score: n }))} data-testid={`program-min-fico-${p.code}`} />
                    ) : p.min_credit_score}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isEditing ? (
                      <NumberInput value={draft.max_dti} suffix="%"
                        onChange={(n) => setDraft((d) => ({ ...d, max_dti: n }))} data-testid={`program-max-dti-${p.code}`} />
                    ) : (p.max_dti != null ? `${p.max_dti}%` : '—')}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isEditing ? (
                      <NumberInput value={draft.min_down_percent} suffix="%"
                        onChange={(n) => setDraft((d) => ({ ...d, min_down_percent: n }))} data-testid={`program-min-down-${p.code}`} />
                    ) : `${p.min_down_percent}%`}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isEditing ? (
                      <MoneyInput value={draft.max_loan_amount}
                        onChange={(n) => setDraft((d) => ({ ...d, max_loan_amount: n }))} data-testid={`program-max-loan-${p.code}`} />
                    ) : fmt$(p.max_loan_amount)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isEditing ? (
                      <NumberInput value={draft.default_rate} suffix="%"
                        onChange={(n) => setDraft((d) => ({ ...d, default_rate: n }))} data-testid={`program-rate-${p.code}`} />
                    ) : `${p.default_rate}%`}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Checkbox checked={isEditing ? !!draft.active : p.active}
                      disabled={!isEditing}
                      onCheckedChange={(v) => setDraft((d) => ({ ...d, active: !!v }))}
                      data-testid={`program-active-${p.code}`} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={!!p.verified_at} onCheckedChange={() => toggleVerified(p)} data-testid={`program-verified-${p.code}`} />
                      <div className="text-[11px] text-slate-400">
                        {p.verified_at ? (
                          <>Verified {formatDate(p.verified_at)}{p.verified_by ? ` by ${p.verified_by}` : ''}</>
                        ) : 'Not verified'}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {isEditing ? (
                      <div className="flex items-center justify-end gap-2">
                        <button type="button" onClick={() => save(p)} disabled={saving}
                          className="text-xs px-2 py-1 rounded bg-[#070B5F] text-white" data-testid={`program-save-${p.code}`}>
                          {saving ? <Loader2 size={12} className="animate-spin" /> : 'Save'}
                        </button>
                        <button type="button" onClick={cancelEdit} className="text-xs px-2 py-1 rounded border border-slate-200" data-testid={`program-cancel-${p.code}`}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <button type="button" onClick={() => startEdit(p)}
                          className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50" data-testid={`program-edit-${p.code}`}>
                          Edit
                        </button>
                        <button type="button" onClick={() => setRetireTarget(p)}
                          className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50" data-testid={`program-retire-${p.code}`}>
                          Retire
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                  {isEditing && (
                    <tr className="border-b border-slate-100 bg-slate-50/60" data-testid={`program-details-${p.code}`}>
                      <td colSpan={10} className="px-4 py-3">
                        <div className="grid gap-4 md:grid-cols-2">
                          <fieldset className="space-y-1">
                            <legend className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Property types allowed</legend>
                            {PROPERTY_TYPES.map(([value, label]) => (
                              <label key={value} className="flex items-center gap-2 text-sm">
                                <Checkbox checked={draft.property_types.includes(value)}
                                  onCheckedChange={(v) => toggleIn('property_types', value, !!v)} data-testid={`program-type-${p.code}-${value}`} />
                                {label}
                              </label>
                            ))}
                          </fieldset>
                          <fieldset className="space-y-1">
                            <legend className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Occupancy allowed</legend>
                            {PROPERTY_USES.map(([value, label]) => (
                              <label key={value} className="flex items-center gap-2 text-sm">
                                <Checkbox checked={draft.property_uses.includes(value)}
                                  onCheckedChange={(v) => toggleIn('property_uses', value, !!v)} data-testid={`program-use-${p.code}-${value}`} />
                                {label}
                              </label>
                            ))}
                            <label className="flex items-center gap-2 text-sm pt-2">
                              <Checkbox checked={!!draft.requires_veteran}
                                onCheckedChange={(v) => setDraft((d) => ({ ...d, requires_veteran: !!v }))} data-testid={`program-veteran-${p.code}`} />
                              Veterans only
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <Checkbox checked={!!draft.first_time_buyer_only}
                                onCheckedChange={(v) => setDraft((d) => ({ ...d, first_time_buyer_only: !!v }))} data-testid={`program-ftb-${p.code}`} />
                              First-time buyers only
                            </label>
                          </fieldset>
                          <div className="space-y-2">
                            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Months of reserves</div>
                            <NumberInput integer value={draft.min_reserves_months}
                              onChange={(n) => setDraft((d) => ({ ...d, min_reserves_months: n }))} data-testid={`program-reserves-${p.code}`} />
                          </div>
                          <div className="space-y-2">
                            <PickSelect label="Mortgage insurance" value={draft.mi?.kind || 'none'} options={MI_KINDS}
                              onChange={setMiKind} testId={`program-mi-kind-${p.code}`} />
                            {draft.mi?.kind === 'fha_mip' && (
                              <NumberInput value={draft.mi.annual_rate} suffix="% a year"
                                onChange={(n) => setDraft((d) => ({ ...d, mi: { ...d.mi, annual_rate: n } }))} data-testid={`program-mi-rate-${p.code}`} />
                            )}
                          </div>
                          <div className="md:col-span-2 space-y-1">
                            <label htmlFor={`program-conditions-${p.code}`} className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                              Conditions shown with results (one per line)
                            </label>
                            <textarea id={`program-conditions-${p.code}`} rows={3} value={draft.conditions.join('\n')}
                              onChange={(e) => setDraft((d) => ({ ...d, conditions: e.target.value.split('\n') }))}
                              className="border border-slate-200 rounded px-2 py-1 text-sm w-full" data-testid={`program-conditions-${p.code}`} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </TableScroller>

      {retired.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-2" data-testid="retired-programs">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Retired programs</div>
          {retired.map((p) => (
            <div key={p.program_id} className="flex items-center justify-between text-sm" data-testid={`retired-row-${p.code}`}>
              <span className="text-slate-600">{p.name} <span className="text-slate-400">retired {formatDate(p.retired_at)}</span></span>
              <button type="button" onClick={() => restore(p)}
                className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50" data-testid={`program-restore-${p.code}`}>
                Restore
              </button>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!retireTarget} onOpenChange={(open) => { if (!open) setRetireTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retire {retireTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              It leaves the program list and every new qualification run. Saved scenarios keep their results. You can restore it from the Retired programs list. This is audited.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="program-retire-confirm" onClick={retire} disabled={busy} className="bg-red-600 hover:bg-red-700">
              Retire
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
